<template>
  <div>
    <BaseSection :title="edit ? '' : data.name" :image="edit ? '' : data.logo">
      <v-form ref="form" lazy-validation class="gap5 grid align-start">
        <div class="d-flex flex-column align-start gap5">
          <BaseCard v-if="edit" width="100%">
            <BaseInput
              :label="t['dialog-add_edit-labels']['nazvanie-kompanii']"
              v-model="data.name"
              :view="!edit"
              :viewValue="data.name || '-'"
              :rules="['required', 'max50']"
            />
            <div class="d-flex align-center gap5">
              <v-avatar :key="String(data.logo)" size="50" color="#f2f2f2">
                <v-img v-if="data.logo" :src="apiDomain + '/min/' + data.logo" contain/>
              </v-avatar>
              <UploadFile
                :tooltip="t['dialog-add_edit-buttons'][data.logo ? 'izmenit-logotip' : 'zagruzit-logotip']"
                :accept="['jpg', 'jpeg', 'png']"
                :on-load="onLoadLogo"
                :multiple="false"
                :minimal="false"
              />
            </div>
          </BaseCard>
          <BaseCard width="100%">
            <BaseInput
              v-for="field in fields[0]"
              :key="field.name"
              :label="field.label"
              v-model="data[field.name]"
              :view="!edit"
              :viewValue="data[field.name] || '-'"
              :rules="field.rules"
            />
          </BaseCard>
          <BaseCard width="100%">
            <GoogleAutocomplete
              v-if="edit"
              :title="t['dialog-adress']['adres-dlya-vystavleniya-schetov']"
              :street="data['street']"
              :zip="data['zip']"
              :city="data['city']"
              :on-change="changeAddress"
              class="flex-grow-1"
            />
            <BaseInput
              v-else
              :label="t['dialog-adress']['adres-dlya-vystavleniya-schetov']"
              :viewValue="(data['street'] ? data['street'] : '-') +
              (data['zip'] ? ', ' + data['zip'] : '') +
              (data['city'] ? ' ' + data['city'] : '')"
              view
            />
          </BaseCard>
        </div>
        <BaseCard :subtitle="t['dialog-add_edit']['elektronnyy-schet-faktura']">
          <BaseInput
            v-for="field in fields[1]"
            :key="field.name"
            :label="field.label"
            v-model="data[field.name]"
            :view="!edit"
            :viewValue="data[field.name] || '-'"
            :rules="field.rules"
          />
        </BaseCard>
        <BaseCard>
          <BaseInput
            type="textarea"
            :label="localStorage.translations.pages.finance['period-table'].notes"
            v-model="data['notes']"
            :view="!edit"
            :viewValue="data['notes'] || '-'"
          />
        </BaseCard>
      </v-form>
    </BaseSection>
    <div class="fixedBtn" v-if="$userCheckAccess({ abilities: 'edit_invoicing_companies' })">
      <BaseTooltip v-if="edit" :tooltip="localStorage.translations.pages.common['buttons']['sohranit']">
        <BaseButton
          :disabled="!navigationGuardianActive"
          @click="onSave"
          round icon_only
          size="60"
          default_icon="button-save"
        />
      </BaseTooltip>
      <BaseTooltip v-else :tooltip="localStorage.translations.pages.common['buttons']['redaktirovat']">
        <BaseButton @click="onEdit" round size="60" icon_only default_icon="button-edit"/>
      </BaseTooltip>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import BaseCard from "components/base/BaseCard";
import UploadFile from "components/UploadFile";
import BaseInput from "components/base/BaseInput";
import GoogleAutocomplete from "components/google/autocomplete/GoogleAutocomplete";

export default {
  name: "clientsCompanySales",
  components: {GoogleAutocomplete, BaseInput, UploadFile, BaseCard},
  computed: {
    ...mapGetters(['apiDomain']),
    ...mapGetters('workClientInvoicingCompany', {'data': 'workClientInvoicingCompanyShow'}),
    ...mapGetters('navigationGuardian', ['navigationGuardianActive', 'navigationGuardianChangeData']),
    edit() {
      return this.$route.name.includes('Edit') || this.createPage
    },
    createPage() {
      return this.$route.name.includes('Create')
    },
    t() {
      return this.localStorage.translations.pages['setting-client']
    },
    fields() {
      return [
        [
          {label: this.t['tables']['vat'], name: 'vat', rules: ['required', 'max20']},
          {label: this.t['tables']['y-tunnus'], name: 'ytunnus', rules: ['required', 'size9']},
        ],
        [
          {label: this.t['tables']['adres-elektronnoy-nakladnoy-ovt'], name: 'ovt', rules: ['required', 'max20']},
          {label: this.t['tables']['id-operatora'], name: 'operator_id', rules: ['required', 'max20']},
          {label: this.t['tables']['operator'], name: 'operator',rules: ['required', 'max50']},
        ],
      ]
    }
  },
  watch: {
    data: {
      handler() {
        if (this.edit) this.onCheckChange({overwrite: false})
      },
      deep: true
    },
    '$route.name'() {
      this.onCheckChange({overwrite: true})
    }
  },
  created() {
    if (!this.createPage) this.onLoadData()
  },
  beforeDestroy() {
    this.onWorkClientInvoicingCompanyClear()
  },
  methods: {
    ...mapActions('workClientInvoicingCompany',
      ['onWorkClientInvoicingCompanyShow', 'onWorkClientInvoicingCompanyUpdate', 'onWorkClientInvoicingCompanyClear',
        'onWorkClientInvoicingCompanyCreate']),
    ...mapActions('navigationGuardian', ['onNavigationGuardianCheck']),
    async onLoadData() {
      await this.onWorkClientInvoicingCompanyShow(this.$route.params.id)
        .then(() => {
          if (this.edit) this.onCheckChange({overwrite: true})
        })
    },
    onCheckChange({overwrite}) {
      this.onNavigationGuardianCheck({
        data: {
          company: this.data
        },
        overwrite,
      })
    },
    changeAddress(data) {
      this.data['street'] = data.street
      this.data['zip'] = data.zip
      this.data['city'] = data.city
    },
    async onSave() {
      if (this.$refs.form.validate()) {
        if (this.data.id) {
          await this.onWorkClientInvoicingCompanyUpdate(this.data)
            .then(() => {
              this.onCheckChange({overwrite: true})
              this.onEdit()
            })
        } else {
          await this.onWorkClientInvoicingCompanyCreate(this.data)
            .then(res => {
              this.onCheckChange({overwrite: true})
              this.$router.push({
                name: this.$route.name.replace('Create', '')
              })
            })
        }
      }
    },
    onEdit() {
      this.$router.push({
        name: this.edit
          ? this.$route.name.replace('Edit', '')
          : this.$route.name + 'Edit'
      })
    },
    onLoadLogo(data) {
      this.data.logo = data[0].url
    }
  }
}
</script>

<style scoped lang="scss">
.grid {
  max-width: 2200px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  @media #{map-get($display-breakpoints, 'md-only')} {
    grid-template-columns: 1fr 1fr;
  }
  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    grid-template-columns: 1fr;
  }
}
</style>